import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Loader2, CheckCircle2, PhoneCall, XCircle, ArrowRight, Phone, Navigation } from 'lucide-react';
import { checkArea } from '@/lib/api';
import { SEO, track } from '@/lib/seo';
import { Reveal } from '@/lib/motion';
import { BUSINESS } from '@/data/site';
import { LOCATIONS, MDVA_AREAS } from '@/data/locations';

const STATES = ['DC', 'MD', 'VA', 'Other'];
const DIRECTIONS_URL = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(`${BUSINESS.name}, ${BUSINESS.address}, ${BUSINESS.city}, ${BUSINESS.state} ${BUSINESS.zip}`);

function AddressChecker() {
  const [form, setForm] = useState({ address: '', city: '', state: 'DC', zip: sessionStorage.getItem('mg_area_zip') || '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await checkArea(form);
      setResult(res);
      if (res.status !== 'invalid') {
        sessionStorage.setItem('mg_area_zip', form.zip);
        sessionStorage.setItem('mg_area_result', JSON.stringify(res));
      }
      track('service_area_check', { zip: form.zip, status: res.status });
    } catch (err) {
      setResult({ status: 'invalid', message: 'Something went wrong — please try again or call us.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 border border-ink/10 bg-bone p-6 sm:p-8">
      <h2 className="text-xl font-extrabold tracking-tight text-ink">Check your address</h2>
      <div>
        <label htmlFor="sa-address" className="text-[10px] font-bold uppercase tracking-[0.25em] text-ash">Street address</label>
        <input id="sa-address" data-testid="service-address-input" value={form.address} onChange={set('address')} placeholder="123 Example St NW" className="mt-2 w-full border border-ink/15 bg-parchment px-4 py-3 text-sm text-ink placeholder:text-ash focus:border-forest focus:outline-none" />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="sa-city" className="text-[10px] font-bold uppercase tracking-[0.25em] text-ash">City</label>
          <input id="sa-city" data-testid="service-city-input" value={form.city} onChange={set('city')} placeholder="Washington" className="mt-2 w-full border border-ink/15 bg-parchment px-4 py-3 text-sm text-ink placeholder:text-ash focus:border-forest focus:outline-none" />
        </div>
        <div>
          <label htmlFor="sa-state" className="text-[10px] font-bold uppercase tracking-[0.25em] text-ash">State</label>
          <select id="sa-state" data-testid="service-state-select" value={form.state} onChange={set('state')} className="mt-2 w-full border border-ink/15 bg-parchment px-4 py-3 text-sm text-ink focus:border-forest focus:outline-none">
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="sa-zip" className="text-[10px] font-bold uppercase tracking-[0.25em] text-ash">ZIP code *</label>
          <input id="sa-zip" data-testid="service-zip-input" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value.replace(/\D/g, '').slice(0, 5) })} placeholder="20004" inputMode="numeric" required className="mt-2 w-full border border-ink/15 bg-parchment px-4 py-3 text-sm text-ink placeholder:text-ash focus:border-forest focus:outline-none" />
        </div>
      </div>
      <button data-testid="service-area-submit-button" type="submit" disabled={loading || form.zip.length !== 5} className="flex w-full items-center justify-center gap-2 bg-forest px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-bone transition-colors hover:bg-moss disabled:opacity-40">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MapPin className="h-4 w-4" /> Check My Address</>}
      </button>

      {result && (
        <div data-testid={`service-result-${result.status}`} className={`flex items-start gap-3 border p-5 text-sm ${result.status === 'served' ? 'border-forest/40 bg-forest/10' : result.status === 'call' ? 'border-gold/50 bg-gold/10' : 'border-oxblood/40 bg-oxblood/10'}`}>
          {result.status === 'served' ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-forest" /> : result.status === 'call' ? <PhoneCall className="mt-0.5 h-5 w-5 shrink-0 text-bronze" /> : <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-oxblood" />}
          <div>
            <p className="font-medium text-ink">{result.message}</p>
            {result.status === 'served' ? (
              <Link to="/shop" data-testid="service-result-shop-link" className="mt-2 inline-block font-semibold text-forest underline underline-offset-4">Start shopping now</Link>
            ) : (
              <a href={BUSINESS.phoneHref} data-testid="service-result-phone-link" className="mt-2 inline-block font-semibold text-forest underline underline-offset-4">Call {BUSINESS.phone}</a>
            )}
          </div>
        </div>
      )}
    </form>
  );
}

export default function ServiceArea() {
  return (
    <>
      <SEO
        title="Areas We Serve — Washington DC, Maryland & Virginia | Majestic Greens DMV"
        description={`Majestic Greens DMV delivers premium cannabis across Washington, DC — Penn Quarter, Capitol Hill, Shaw, Dupont Circle and more — plus parts of MD & VA. Pickup at ${BUSINESS.address}. Call ${BUSINESS.phone}.`}
        path="/service-area"
      />
      <div data-testid="service-area-page" className="pb-28">
        <div className="border-b border-ink/10 bg-parchment py-20">
          <div className="mx-auto max-w-7xl px-6">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-bronze">Serving DC, Maryland & Virginia</p>
              <h1 className="mt-4 text-4xl font-black tracking-[-0.02em] text-ink sm:text-6xl">Areas we serve</h1>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-sage">
                Majestic Greens DMV proudly delivers across Washington, DC and nearby Maryland and Virginia communities — and pickup is always available at {BUSINESS.address}. Find your neighborhood below, or check your exact address instantly.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-6 pt-16">
          <div className="grid gap-12 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Reveal>
                <h2 className="text-2xl font-extrabold tracking-tight text-ink">Washington, DC — areas served</h2>
                <div className="mt-6 grid grid-cols-2 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-3">
                  {LOCATIONS.map((loc) => (
                    <Link key={loc.slug} to={`/${loc.slug}`} data-testid={`area-dc-${loc.slug}`} className="group flex items-center justify-between bg-bone px-4 py-3.5 transition-colors hover:bg-parchment">
                      <span className="text-sm font-semibold text-sage group-hover:text-ink">{loc.name}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-ash transition-all group-hover:translate-x-1 group-hover:text-forest" />
                    </Link>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.1}>
                <h2 className="mt-14 text-2xl font-extrabold tracking-tight text-ink">Maryland & Virginia — call to confirm</h2>
                <p className="mt-2 text-sm text-sage">
                  We serve parts of MD and VA near the District. Call <a href={BUSINESS.phoneHref} className="font-semibold text-forest underline underline-offset-4">{BUSINESS.phone}</a> and we'll confirm your address right away.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {MDVA_AREAS.map((a) => (
                    <a key={a.name} href={BUSINESS.phoneHref} data-testid={`area-mdva-${a.name.toLowerCase().replace(/\s+/g, '-')}`} className="border border-ink/10 bg-parchment px-4 py-2 text-sm font-semibold text-sage transition-colors hover:border-forest/40 hover:text-forest">
                      {a.name}, {a.state}
                    </a>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.15}>
                <h2 className="mt-14 text-2xl font-extrabold tracking-tight text-ink">Neighborhood location guides</h2>
                <div className="mt-6 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2">
                  {LOCATIONS.map((loc) => (
                    <Link key={loc.slug} to={`/${loc.slug}`} data-testid={`guide-${loc.slug}`} className="group flex flex-col bg-bone p-6 transition-colors hover:bg-parchment">
                      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-ash">Washington, DC</span>
                      <h3 className="mt-2 text-lg font-extrabold tracking-tight text-ink group-hover:text-forest">Cannabis near {loc.name}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-sage">Premium flower, vapes, edibles and concentrates near {loc.landmarks}.</p>
                      <span className="mt-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-forest">
                        View Guide <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </span>
                    </Link>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="space-y-6">
              <Reveal delay={0.1}>
                <AddressChecker />
              </Reveal>
              <Reveal delay={0.2}>
                <div className="border border-ink/10 bg-forest p-8 text-bone">
                  <h2 className="text-xl font-extrabold tracking-tight">Find us on the map</h2>
                  <address className="mt-4 text-sm not-italic leading-relaxed text-bone/80">
                    {BUSINESS.name}<br />
                    {BUSINESS.address}<br />
                    {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}
                  </address>
                  <div className="mt-6 space-y-3">
                    <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" data-testid="directions-link" className="flex items-center gap-2 bg-bone px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-gold">
                      <Navigation className="h-4 w-4" /> Get Directions
                    </a>
                    <a href={BUSINESS.phoneHref} data-testid="service-page-phone" onClick={() => track('phone_click', { source: 'service_area' })} className="flex items-center gap-2 border border-bone/30 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-bone transition-colors hover:border-bone">
                      <Phone className="h-4 w-4" /> {BUSINESS.phone}
                    </a>
                  </div>
                  <p className="mt-5 text-xs text-bone/60">${BUSINESS.minOrder} minimum order · Adults 21+ · Pickup & delivery</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
